import { useMemo, useState } from "react";
import { differenceWith } from "lodash";
import { Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";

const RecipientSelector = ({ recipients, setRecipients, allRecipients }) => {
  const [selection, setSelection] = useState(null);
  const anchorRef = useComboboxAnchor();

  const options = useMemo(
    () =>
      differenceWith(allRecipients, recipients, (a, b) => a.id === b.id).map(
        r => ({ value: r.id, label: r.name })
      ),
    [allRecipients, recipients]
  );

  const handleAdd = () => {
    if (selection == null) {
      return;
    }
    const recipient = allRecipients.find(r => r.id === selection.value);
    if (recipient && !recipients.some(r => r.id === recipient.id)) {
      setRecipients([...recipients, recipient]);
    }
    setSelection(null);
  };

  const handleDelete = id => {
    setRecipients(recipients.filter(r => r.id !== id));
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <Label>Recipient</Label>
        <div className="tw:flex tw:items-end tw:gap-2">
          <Combobox
            items={options}
            value={selection}
            onValueChange={setSelection}
            itemToStringLabel={item => item?.label ?? ""}
          >
            <div ref={anchorRef} className="tw:flex-1">
              <ComboboxInput
                placeholder="Select…"
                showTrigger={false}
                className="tw:w-full"
              />
            </div>
            <ComboboxContent anchor={anchorRef}>
              <ComboboxEmpty>No matches</ComboboxEmpty>
              <ComboboxList>
                {item => (
                  <ComboboxItem key={item.value} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          <Button
            type="button"
            variant="outline"
            onClick={handleAdd}
            disabled={selection == null}
          >
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </div>
      </div>
      {recipients.length === 0 ? (
        <p className="tw:py-2 tw:text-center tw:text-sm tw:text-[var(--color-text-muted)] tw:italic">
          No Recipients included in this preset
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-1">
          {recipients.map(({ id, name, types }) => (
            <div
              key={id}
              className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface-overlay)] tw:px-2 tw:py-1.5 tw:text-[var(--color-text-primary)]"
            >
              <span className="tw:flex-1">
                {name}
                {types?.length > 0 && (
                  <span className="tw:ml-2 tw:inline-flex tw:gap-1">
                    {types.map(t => (
                      <Badge key={t} variant="outline" size="sm">
                        {t}
                      </Badge>
                    ))}
                  </span>
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remove"
                onClick={() => handleDelete(id)}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipientSelector;
